import { memo, useMemo, type ReactNode } from 'react';

import { cn } from '@/lib/cn';

interface Props {
  children: string;
  className?: string;
}

interface TextPart {
  text: string;
  bold?: boolean;
  italic?: boolean;
}

/**
 * Tiny inline-markdown renderer for paragraph / list-item text. Supports
 *   **bold**, *italic*, and strips citation markers like `[1]` (the old
 *   `CitationText` flow rendered those separately; it was removed as dead
 *   code, so today they are only stripped).
 *
 * Ported from mobile-app/src/components/news/blocks/MarkdownText.tsx.
 */
function parseInlineMarkdown(raw: string): TextPart[] {
  // Strip citation markers [1], [2] etc.
  const text = raw.replace(/\[\d+\]/g, '');

  const parts: TextPart[] = [];
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ text: text.slice(lastIndex, match.index) });
    }
    if (match[2]) {
      parts.push({ text: match[2], bold: true });
    } else if (match[3]) {
      parts.push({ text: match[3], italic: true });
    }
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push({ text: text.slice(lastIndex) });
  }

  return parts.length > 0 ? parts : [{ text }];
}

export const MarkdownText = memo(function MarkdownText({ children, className }: Props) {
  const parts = useMemo(() => parseInlineMarkdown(children), [children]);

  if (parts.length === 1 && !parts[0].bold && !parts[0].italic) {
    return <span className={className}>{parts[0].text}</span>;
  }

  const nodes: ReactNode[] = parts.map((part, i) => {
    if (part.bold) {
      return (
        <strong key={i} className="font-bold">
          {part.text}
        </strong>
      );
    }
    if (part.italic) {
      return (
        <em key={i} className="italic">
          {part.text}
        </em>
      );
    }
    return <span key={i}>{part.text}</span>;
  });

  return <span className={cn(className)}>{nodes}</span>;
});
